import React from "react";

import { cn } from "../../../common/u1/cn";
import { convertLetterCase } from "../../../../utils/Helper";
/* The authoritative display prices, shared with the checkout. */
import {
  WEB_PROFILE_MEMBER,
  PROFILE_WINDOW_DAYS,
} from "../../../../constants/webProfilePricing";
import EnterpriseAddCompany from "../../../common/EnterpriseAddCompany";
import {
  BellIcon,
  LockIcon,
  BuildingIcon,
  ClockIcon,
  StatusGlyph,
  Dirham,
} from "../../../../assets/ui/icons";
import { profileStatus, VERIFIED } from "./profileStatus";
import { ProfileOwnerCta } from "./ProfileOwnerCta";

/* The masthead's calls to action, as the design's three cards. */

/* Transcribed: the enterprise button reports the order's status once there is
   one, title-cased, and otherwise invites the viewer to add the company. */
export const enterpriseButtonText = (purchaseDetails) =>
  purchaseDetails?.status
    ? convertLetterCase(purchaseDetails.status.split("_").join(" "), "titlecase")
    : "Add to Enterprise";

/* The subscription state, TRANSCRIBED from the live banner's chain (BannerCompanyCard:1602-1630). */
export const SUBSCRIPTION_IN_PROGRESS = "Subscription In Progress";

export const subscriptionState = ({
  hasSubscription,
  personalPurchase,
  worldboxAvailable,
  purchaseDetails,
}) => {
  if (!hasSubscription || !personalPurchase) return null;

  if (worldboxAvailable) {
    return purchaseDetails?.purchaseType === "LIFETIME_PARTNER_PROGRAM"
      ? "Subscribed"
      : `Subscribed Till : ${purchaseDetails?.expireDate}`;
  }
  return SUBSCRIPTION_IN_PROGRESS;
};

/* Paid for, but the profile is not open yet. */
export const isSubscriptionPending = (subscription) =>
  subscription === SUBSCRIPTION_IN_PROGRESS;

const CARD = "flex flex-col rounded-[14px] border p-4 sm:p-5";
const HEAD = "flex items-center gap-2.5";
const TITLE = "m-0 text-[15px] font-bold text-[var(--ink)] font-sans";
const BODY = "mt-2 mb-4 text-[13px] leading-[1.5] text-[var(--muted)] font-sans";

const Glyph = ({ children, tone }) => (
  <span
    aria-hidden="true"
    className={cn(
      "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border",
      tone
    )}
  >
    {children}
  </span>
);

export const ProfileActionCards = ({
  company,
  role,
  actions,
  onBuyFullProfile,
  /* The FLATTENED purchase details, the mapped statusData, and the trigger pair — all from usePurchaseStatus. */
  purchase,
  /* The owner's own chain — Get Membership / Get Verified / the two
     in-progress states. See useOwnerCta. Absent for a viewer who does not own
     the company, which is the common case. */
  owner,
  className = "",
}) => {
  const {
    isFollowing,
    busy,
    follow,
    unfollow,
    claim,
    canClaim,
    companyExists,
    userEnterprise,
    showAddToEnterprise,
    hasSubscription,
  } = actions || {};

  const {
    purchaseDetails,
    statusData,
    trigger,
    setTrigger,
    personalPurchase,
    worldboxAvailable,
  } = purchase || {};

  const subscription = subscriptionState({
    hasSubscription,
    personalPurchase,
    worldboxAvailable,
    purchaseDetails,
  });

  const subscriptionPending = isSubscriptionPending(subscription);

  const isVerified = profileStatus(company) === VERIFIED;
  /* Transcribed: a manager of a verified company is shown the badge rather
     than an offer to buy. */
  const showVerified = isVerified && Boolean(role);

  /* Transcribed outer branch (BannerCompanyCard:1550): when the viewer is linked to this company and the link is claimed, this slot is THEIRS — the whole... */
  const ownerCta = owner?.isOwnCompany ? owner?.kind : null;
  const showFollow = !companyExists;
  const showClaim = !companyExists && canClaim;

  /* With the owner's control moved onto the More row, an owner can be left with no cards at all — and an empty grid still occupies its gap, leaving a band of... */
  if (!showFollow && !showClaim && ownerCta) return null;

  return (
    <div
      className={cn(
        "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3",
        className
      )}
    >
      {showFollow && (
        <section className={cn(CARD, "border-[var(--line)] bg-white")}>
          <div className={HEAD}>
            <Glyph tone="border-[var(--line)] bg-[var(--bg)] text-[var(--coral)]">
              <BellIcon size={17} />
            </Glyph>
            <h3 className={TITLE}>Follow updates</h3>
          </div>
          <p className={BODY}>
            Free. Get notified when any section of this profile changes.
          </p>
          <button
            type="button"
            onClick={isFollowing ? unfollow : follow}
            disabled={busy}
            className={cn(
              "mt-auto h-11 w-full rounded-[10px] border text-[14px] font-semibold font-sans",
              "transition-colors focus-visible:outline-none focus-visible:ring-2",
              "focus-visible:ring-[var(--coral)] disabled:cursor-not-allowed disabled:opacity-60",
              isFollowing
                ? "border-[var(--coral)] bg-[var(--coral-soft)] text-[var(--coral-deep)]"
                : "border-[var(--line)] bg-white text-[var(--ink)] hover:border-[var(--line-2)]"
            )}
          >
            {isFollowing ? "Unfollow" : "Follow"}
          </button>
        </section>
      )}

      {/* The acquisition card. Its face changes with what the viewer already has, which is the live banner's behaviour. NOT drawn for the company's own people: their... */}
      {!ownerCta && (
      <section
        className={cn(
          CARD,
          ownerCta || showVerified || subscription
            ? "border-[var(--line)] bg-white"
            : "border-[var(--orange-border)] bg-[var(--orange-soft)]"
        )}
      >
        <div className={HEAD}>
          <Glyph
            tone={
              ownerCta === "verified" || showVerified
                ? "border-[var(--verified-border)] bg-[var(--success-soft)] text-[var(--success)]"
                : "border-[var(--orange-border)] bg-white text-[var(--coral)]"
            }
          >
            {ownerCta === "verified" || showVerified ? (
              <StatusGlyph kind="verified" size={16} />
            ) : ownerCta ? (
              <BuildingIcon size={17} />
            ) : subscriptionPending ? (
              <ClockIcon size={17} />
            ) : (
              <LockIcon size={17} />
            )}
          </Glyph>
          <h3 className={TITLE}>
            {ownerCta
              ? "Your business"
              : showVerified
              ? "Verified by TREVEX"
              : subscriptionPending
              ? "Web Profile in progress"
              : subscription
              ? "Your access"
              : "Buy a Web Profile"}
          </h3>
        </div>

        <p className={BODY}>
          {ownerCta
            ? ownerCta === "verified"
              ? "You manage this profile and TREVEX has verified it."
              : "You manage this profile."
            : showVerified
            ? "You manage this profile and TREVEX has verified it."
            : subscriptionPending
            ? "Payment received. This business has nothing on file yet, so our team is compiling it — your 30 days start once the profile opens, and we'll let you know."
            : subscription
            ? "You already have access to the full profile."
            : `See the entire profile — financials, ownership, documents — for ${PROFILE_WINDOW_DAYS} days.`}
        </p>

        <div className="mt-auto flex flex-col gap-2.5">
          {ownerCta ? (
            <ProfileOwnerCta
              kind={ownerCta}
              role={role}
              submitting={owner?.submitting}
              onGetVerified={owner?.getVerified}
              onGetMembership={owner?.getMembership}
            />
          ) : showVerified ? (
            <span
              className={cn(
                "inline-flex h-11 items-center justify-center gap-2 rounded-[10px]",
                "border border-[var(--verified-border)] bg-[var(--success-soft)]",
                "text-[14px] font-semibold text-[var(--success)] font-sans"
              )}
            >
              <StatusGlyph kind="verified" size={14} />
              Verified
            </span>
          ) : subscription ? (
            <span
              className={cn(
                "inline-flex h-11 items-center justify-center gap-2 rounded-[10px] border",
                "text-[13.5px] font-semibold font-sans",
                subscriptionPending
                  ? "border-[var(--warning-soft)] bg-[var(--warning-soft)] text-[var(--gold-deep)]"
                  : "border-[var(--line)] bg-white text-[var(--ink-2)]"
              )}
            >
              {subscriptionPending && (
                <span aria-hidden="true" className="flex shrink-0">
                  <ClockIcon size={14} />
                </span>
              )}
              {subscriptionPending ? "In progress" : subscription}
            </span>
          ) : (
            <button
              type="button"
              onClick={onBuyFullProfile}
              className={cn(
                "inline-flex h-11 w-full items-center justify-center gap-1 rounded-[10px]",
                "bg-[var(--coral)] text-[14px] font-semibold text-white font-sans",
                "shadow-[shadow:var(--coral-glow)] transition-colors",
                "hover:bg-[var(--orange-hover)] focus-visible:outline-none",
                "focus-visible:ring-2 focus-visible:ring-[var(--coral)] focus-visible:ring-offset-2"
              )}
            >
              From <Dirham />
              {WEB_PROFILE_MEMBER.toFixed(2)} / {PROFILE_WINDOW_DAYS} days
            </button>
          )}

          {/* Not in the design; the other way to acquire the profile, so it belongs beside the buy. */}
          {showAddToEnterprise && (
            <div
              className={cn(
                "[&>div>button]:h-11 [&>div>button]:w-full",
                "[&>div>button]:justify-center [&>div>button]:rounded-[10px]"
              )}
            >
            <EnterpriseAddCompany
              getPurchaseDetails={purchaseDetails}
              EnterpriseButtonText={enterpriseButtonText(purchaseDetails)}
              /* Required in practice: the component calls setTriggerPurchaseApi after adding or ordering (EnterpriseAddCompany.jsx:151, 205), so omitting it throws the... */
              triggerPurchaseApi={trigger}
              setTriggerPurchaseApi={setTrigger}
              statusData={statusData}
              companyDetails={company}
              enterpriseData={userEnterprise}
            />
            </div>
          )}
        </div>
      </section>
      )}

      {showClaim && (
        <section className={cn(CARD, "border-[var(--line)] bg-white")}>
          <div className={HEAD}>
            <Glyph tone="border-[var(--line)] bg-[var(--bg)] text-[var(--navy)]">
              <BuildingIcon size={17} />
            </Glyph>
            <h3 className={TITLE}>Is this your business?</h3>
          </div>
          <p className={BODY}>
            Request access as the authorized representative and manage this profile.
          </p>
          <button
            type="button"
            onClick={claim}
            className={cn(
              "mt-auto h-11 w-full rounded-[10px] bg-[var(--navy)] text-[14px]",
              "font-semibold text-white font-sans transition-colors",
              "hover:bg-[var(--navy-hover)] focus-visible:outline-none focus-visible:ring-2",
              "focus-visible:ring-[var(--navy)] focus-visible:ring-offset-2"
            )}
          >
            Request access
          </button>
        </section>
      )}
    </div>
  );
};

/* The previous name, kept so the page and its tests keep working. */
export const ProfileActionBar = ProfileActionCards;

export default ProfileActionCards;
